import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSpecificMaritimeArea } from '../../api';
import ExitFocusRegionButton from '../../common-components/conflict-zone-chip/exit-focus-region-button';
import PanelTitle from '../../common-components/panel/panel-title';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  EMenuItems,
  setSelectedOption,
  setThreatAssessmentOpen,
} from '../../main-menu/menu.slice';
import { addHistoryItem } from '../../nav-history.slice';
import { setSelectedEntityThreatAssessmentInfo } from '../../dossiers_and_panels/threat-assessments/threat-assessment.slice';
import { kebabify } from '../../utils/text-formatting.utils';

const NS_IRAN_CONFLICT_URL =
  'https://north-standard.com/insights-and-resources/iran-conflict';
const NS_CONTACT_URL = 'https://north-standard.com/our-people';
const NS_INCHCAPE_URL =
  'https://www.iss-shipping.com/advisories/middle-east-port-operations-update/';
const NS_GAC_URL =
  'https://www.gac.com/hot-port-news/update-on-the-middle-east-situation';

function IranConflictPanel() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const ports = useAppSelector((state) => state.ports.ports);
  const correspondents = useAppSelector(
    (state) => state.correspondents.correspondents
  );
  const incidents = useAppSelector((state) => state.incidents.incidents);
  const industryNews = useAppSelector(
    (state) => state.industryNews.industryNews
  );
  const areas = useAppSelector((state) => state.riMaritimeAreas.areas);

  const [pgHeadline, setPgHeadline] = useState<string | null>(null);
  const [pgHeadlineLoading, setPgHeadlineLoading] = useState(false);

  const fetchHeadline = useCallback(async () => {
    const persianGulf = areas?.find((a) =>
      a.title.toLowerCase().includes('persian gulf')
    );
    if (!persianGulf) return;
    setPgHeadlineLoading(true);
    try {
      const extendedArea = await getSpecificMaritimeArea(persianGulf.id);
      setPgHeadline((prev) =>
        prev !== extendedArea.headline ? extendedArea.headline : prev
      );
    } finally {
      setPgHeadlineLoading(false);
    }
  }, [areas]);

  useEffect(() => {
    fetchHeadline();
  }, [fetchHeadline]);

  const loadingDatasets = [
    !ports && 'Ports',
    !correspondents && 'Correspondents',
    !incidents && 'Incidents',
    !industryNews && 'News',
    !areas && 'Maritime Areas',
  ].filter(Boolean) as string[];

  /** Panel-specific cleanup run after focus region is deactivated: close the panel and return to the map. */
  const handleAfterExit = () => {
    dispatch(setSelectedOption(EMenuItems.NOTHING_SELECTED));
    navigate('/');
  };

  const openPersianGulfThreatAssessment = () => {
    const persianGulf = areas?.find((a) =>
      a.title.toLowerCase().includes('persian gulf')
    );
    if (!persianGulf) {
      // Fall back to the maritime areas panel if area not loaded yet
      dispatch(setSelectedOption(EMenuItems.RI_MARITIME_AREAS));
      navigate(`/${kebabify(EMenuItems.RI_MARITIME_AREAS)}`);
      return;
    }
    getSpecificMaritimeArea(persianGulf.id).then((extendedArea) => {
      dispatch(
        setSelectedEntityThreatAssessmentInfo({
          id: extendedArea.threatAssessment,
          title: extendedArea.title,
        })
      );
      dispatch(addHistoryItem({ type: 'iranConflict' }));
      dispatch(setThreatAssessmentOpen(true));
    });
  };

  return (
    <Box
      data-testid="iran-conflict-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <PanelTitle>Iran Conflict</PanelTitle>

      {/* Exit conflict view — uses shared ExitFocusRegionButton with panel-close callback */}
      <ExitFocusRegionButton onAfterExit={handleAfterExit} />

      {/* Loading indicator while datasets are being fetched */}
      {loadingDatasets.length > 0 && (
        <Box
          data-testid="iran-conflict-loading-indicator"
          sx={{
            mx: 2,
            mt: 1,
            mb: 0,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <CircularProgress size={14} color="inherit" />
          <Typography variant="caption" color="text.secondary">
            Loading {loadingDatasets.join(', ')}…
          </Typography>
        </Box>
      )}

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" align="justify" sx={{ mb: 2, mt: 1.5 }}>
          This &apos;Focus Region&apos; view brings together the latest
          intelligence and guidance relating to the evolving situation in the
          Persian Gulf. It combines Risk Intelligence threat assessments,
          maritime incident data, and correspondent coverage with access to
          relevant news, and articles from our website. This view will update as
          we receive more information.
        </Typography>

        {/* Contact */}
        <Typography variant="body2" align="justify" sx={{ mb: 1 }}>
          Members with concerns or queries about entered vessels should contact
          their{' '}
          <Link
            href={NS_CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="secondary.main"
            underline="hover"
          >
            usual contacts
          </Link>{' '}
          at the Club for guidance.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle2"
          align="justify"
          fontWeight="600"
          sx={{ mb: 1 }}
        >
          Threat Assessment
        </Typography>
        <Typography variant="body2" align="justify" sx={{ mb: 1 }}>
          {pgHeadlineLoading || pgHeadline === null ? (
            <>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="60%" />
            </>
          ) : (
            pgHeadline
          )}
        </Typography>
        <Tooltip
          title={
            !areas || areas.length === 0
              ? 'Loading maritime areas…'
              : 'Open the Persian Gulf threat assessment from Risk Intelligence.'
          }
        >
          <span>
            <Button
              fullWidth
              disabled={!areas || areas.length === 0}
              sx={{ marginBottom: '1rem' }}
              onClick={openPersianGulfThreatAssessment}
              data-testid="iran-conflict-ri-maritime-areas-button"
            >
              Threat Assessment
            </Button>
          </span>
        </Tooltip>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle2"
          align="justify"
          fontWeight="600"
          sx={{ mb: 1 }}
        >
          Key Resources
        </Typography>
        <List dense disablePadding sx={{ mb: 2, listStyleType: 'disc', pl: 4 }}>
          <ListItem disablePadding sx={{ display: 'list-item' }}>
            <Typography variant="body2" align="justify" sx={{ mb: 1 }}>
              Visit our{' '}
              <Link
                href={NS_IRAN_CONFLICT_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="secondary.main"
                underline="hover"
              >
                Iran Conflict webpage
              </Link>{' '}
              for detailed guidance and updates
            </Typography>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'list-item' }}>
            <Typography variant="body2" align="justify" sx={{ mb: 1 }}>
              <Link
                href={NS_INCHCAPE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="secondary.main"
                underline="hover"
              >
                Live operational updates
              </Link>{' '}
              from Inchcape Shipping Services
            </Typography>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'list-item' }}>
            <Typography variant="body2" align="justify" sx={{ mb: 1 }}>
              <Link
                href={NS_GAC_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="secondary.main"
                underline="hover"
              >
                Regional situation updates
              </Link>{' '}
              from GAC
            </Typography>
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Comment about exiting focus region */}
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, fontStyle: 'italic' }}
        >
          Note: Activating this mode will automatically limit other
          functionality, providing a focused view of Persian Gulf related
          information. To return to the full map view, or use other
          functionality, simply click the &apos;Exit Focus Region&apos; button.
        </Typography>
      </Box>
    </Box>
  );
}

export default IranConflictPanel;
